import { Button, Card, Col, Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useState, useEffect } from "react";
import _ from "lodash";
import SectionHeader from '../Common/SectionHeader';
import PerfectScrollbar from 'react-perfect-scrollbar';
import ImageCarousel from './ImageCarousel';
import { dropDowns } from '../../../Common/EquipmentJson';
import SensorAndBearingList from "./SensorAndBearingList";
import { Icon } from "@iconify/react";
import ToolTip from "../Common/Tooltip";
import {
    SET_COMPONENT_LOADING, SET_ONGOING_EDIT_GLOBALLY,
    SET_SELECTED_COMPONENT_DETAILS, UPDATE_COMPONENT_INFORMATION, 
    UPDATE_COMPONENT_NAME
} from "../../../store/actions";
import { useMutation, useQuery } from "react-query";
import { checkForAPISuccess, checkForExpiredJwtResponse, showErrorToast, showInfoToast, showSuccessToast } from "../../../Common/commonFunctions";
import AddMoreInformationField from "./AddMoreInformationField";
import { updateComponentById, fetchComponentAndSensorInformation, renameEntity } from "../../../api";
import ComponentRevertPopup from './ComponentRevertPopup';
import { IMAGE_OPTIONS_TYPE, RESPONSE_TYPE_VALIDATION_ERROR_CODE, RESPONSE_COMPONENT_RENAME_FAILED_CODE } from "../../../Common/constants";
import ExpandedImageView from "./ExpandedImageView";
import { ClipLoader } from "react-spinners";
import BearingForm from "../Bearing/BearingForm";
import { sensorJson } from "../../../Common/sensorData";
import BearingList from "../Bearing/BearingList"

const ComponentInformation = props => {

    const dispatch = useDispatch();
    const selectedComponent = useSelector(state => state.equipment.selectedComponent);
    const isEditOngoing = useSelector(state => state.equipment.isEditOngoing);
    const isLockedProfile = useSelector(state => state.equipment.isLockedProfile);
    const currentEnlargedImageType = useSelector(state => state.equipment.currentEnlargedImageType);
    const allEquipments = useSelector(state => state.equipment.allEquipments);

    const [utils, setUtils] = useState({
        metadata: {}, metadataInitial: {}, id: "",
        componentName: { initial: "", current: "" }, isEditActive: false,
        showAddMoreInfo: false, images: {},
        showRevertPopup: false, installTimeMetadata: {},
        showBearingForm: false, indexOfVisibleBearingInfo: undefined,
        availableLocations: [], isNameEditActive: false,
        typeValidationErrors: {}, categoryMetadata: {}
    });

    const [bearings, setBearings] = useState([]);

    const useQueryConfiguration = {
        enabled: false, retry: false, retryOnMount: false,
        refetchOnMount: false, refetchOnWindowFocus: false
    }

    const getComponentSensor = async () => await fetchComponentAndSensorInformation(selectedComponent?.id);
    const updateComponent = async () => await updateComponentById(selectedComponent?.id, utils.metadata);

    const updateComponentName = async () => {
        const response = await renameEntity("component", { name: utils.componentName.current, id: selectedComponent.id });
        if(checkForAPISuccess(response?.data?.statusCode)) {
            showSuccessToast("Updated successfully");
            dispatch({ type: UPDATE_COMPONENT_NAME, payload: { id: selectedComponent.id, name: utils.componentName.current } });
            setUtils({ ...utils, componentName: { ...utils.componentName, initial: utils.componentName.current }, isNameEditActive: false });
        }
        else if(response?.data?.statusCode === RESPONSE_COMPONENT_RENAME_FAILED_CODE) {
            showInfoToast("Provided name is already taken by a different component in this equipment unit. Please provide different name", 7000);
        }
        else {
            showErrorToast("Failed to update component name");
        }
    }

    const getComponentSensorQuery = useQuery("get-component-sensor", getComponentSensor, {...useQueryConfiguration});
    const updateComponentQuery = useQuery('update-component-information', updateComponent, {...useQueryConfiguration});
    const updateComponentNameMutation = useMutation(updateComponentName);

    useEffect(() => {
        if(getComponentSensorQuery.isFetched) {
            if(checkForAPISuccess(getComponentSensorQuery?.data?.data?.statusCode)) {
                const response = _.cloneDeep(getComponentSensorQuery?.data?.data?.data);
                dispatch({
                    type: SET_SELECTED_COMPONENT_DETAILS,
                    payload: {
                        id: response.id, class: response.componentClass, metadata: response.metadata,
                        name: response.name, sensors: response.sensors, installTimeMetadata: response.installTimeMetadata,
                        componentPictures: { ...response.pictures }, labelPictures: { ...response.labelPictures },
                        bearings: response.bearings, categoryMetadata: response.categoryMetadata
                    }
                });
            }
            else {
                showErrorToast("Failed to fetch component");
            }
            getComponentSensorQuery.remove();
        }
        else if(getComponentSensorQuery.isFetching) {
            dispatch({ type: SET_COMPONENT_LOADING, payload: true });
        }
    }, [getComponentSensorQuery.isFetched]);

    useEffect(() => {
        if(updateComponentQuery.isFetched) {
            if(updateComponentQuery.isSuccess && checkForAPISuccess(updateComponentQuery?.data?.data?.statusCode)) {
                dispatch({ type: UPDATE_COMPONENT_INFORMATION, payload: utils.metadata });
                showSuccessToast("Component information updated successfully");
            }
            else if(updateComponentQuery?.data?.data?.statusCode === RESPONSE_TYPE_VALIDATION_ERROR_CODE) {
                setUtils({ ...utils, typeValidationErrors: updateComponentQuery.data.data?.data });
            }
            else {
                if(!checkForExpiredJwtResponse(updateComponentQuery?.data?.data?.statusCode)) {
                    setUtils({ ...utils, isEditActive: false, metadata: _.cloneDeep(utils.metadataInitial), showAddMoreInfo: false });
                    dispatch({ type: SET_ONGOING_EDIT_GLOBALLY, payload: false });
                    showErrorToast("Failed to update component information");
                }
            }
            updateComponentQuery.remove();
        }
    }, [updateComponentQuery.isFetched]);

    useEffect(() => {
        if(!_.isEmpty(props?.equipmentId) && !_.isEmpty(selectedComponent?.id) && selectedComponent.id !== utils.id) {
            getComponentSensorQuery.refetch();
        }
        else {
            setUtils({});
        }
    }, [selectedComponent?.id]);

    useEffect(() => {
        if(!getComponentSensorQuery.isFetching && !_.isEmpty(selectedComponent?.id) && !_.isEmpty(props?.equipmentId)) {
            setUtils({
                ...utils, isEditActive: false, showAddMoreInfo: false, id: selectedComponent?.id,
                metadataInitial: selectedComponent.metadata, componentName: { initial: selectedComponent.name, current: selectedComponent.name },
                metadata: selectedComponent.metadata, installTimeMetadata: selectedComponent.installTimeMetadata,
                availableLocations: sensorJson[selectedComponent.class]?.install_locations?.values,
                typeValidationErrors: {}, categoryMetadata: selectedComponent.categoryMetadata,
                showBearingForm: false
            });
            setBearings(selectedComponent.bearings);
        }
    },[selectedComponent]);

    const checkIfCurrentEquipmentIsCloned = () => {
        if(!_.isEmpty(allEquipments) && !_.isEmpty(props?.equipmentId)) {
            for(var i = 0; i < allEquipments.length; i++) {
                if(_.isEqual(allEquipments[i].id, props.equipmentId)) {
                    return allEquipments[i]?.isCloned;
                }
            }
        }
        return false;
    }

    const handleIconClickEvents = (action) => {
        switch(action) {

            case "EDIT-METADATA":
                setUtils({ ...utils, isEditActive: true });
                dispatch({ type: SET_ONGOING_EDIT_GLOBALLY, payload: true });
            break;

            case "SAVE-METADATA":
                updateComponentQuery.refetch();
            break;

            case "CANCEL-METADATA-SAVE":
                setUtils({ ...utils, isEditActive: false, metadata: utils.metadataInitial, showAddMoreInfo: false, typeValidationErrors: {} });
                dispatch({ type: SET_ONGOING_EDIT_GLOBALLY, payload: false });
            break;

            case "ADD-BEARING":
                setUtils({...utils, showBearingForm : true});
            break;

            case "REVERT":
                if(checkIfCurrentEquipmentIsCloned()) {
                    showInfoToast("Install time info not available for this equipment as it has been cloned from another equipment. Revert option not available", 7000);
                }
                else {
                    setUtils({ ...utils, showRevertPopup: true });
                }
            break;

            case "EDIT-NAME":
                dispatch({ type: SET_ONGOING_EDIT_GLOBALLY, payload: true });
                setUtils({ ...utils, isNameEditActive: true });
            break;

            case "SAVE-NAME":
                updateComponentNameMutation.mutate();
            break;

            case "CANCEL-NAME-SAVE":
                dispatch({ type: SET_ONGOING_EDIT_GLOBALLY, payload: false });
                setUtils({ ...utils, isNameEditActive: false, componentName: { ...utils.componentName, current: utils.componentName.initial } });
            break;

            default:
            break;
        }
    }

    const returnIcon = (toolTipKey, toolTipValue, action, icon, additionalClassNames, width, height, color) => 
        <ToolTip
            toolTipKey={toolTipKey} toolTipValue={toolTipValue}
            overlayComponent={
                <Icon
                    color={color ? color : "#075978"}
                    onClick={() => handleIconClickEvents(action)}
                    className={`cursor-pointer ${additionalClassNames}`}
                    icon={icon}
                    width={width ? width : 15}
                    height={height ? height : 15}
                />
            }
        />

    return (
            <>
                {
                    !_.isEmpty(currentEnlargedImageType) && _.isEqual(currentEnlargedImageType, IMAGE_OPTIONS_TYPE[1]) &&
                        <ExpandedImageView target={IMAGE_OPTIONS_TYPE[1]} title={utils.componentName?.current} />
                }
                <Col
                    className={`p-1 ${_.isEqual(currentEnlargedImageType, IMAGE_OPTIONS_TYPE[0]) ? "display-none" : ""}`}
                    {...props.breakPoints}
                >
                    <Card className="p-0 h-100">
                        <Card.Header className="h-50-px">
                            {
                                utils.isNameEditActive ?
                                    updateComponentNameMutation.isLoading ?
                                        <ClipLoader size={25} color="#075978" />
                                            :
                                        <div className="vertical-center-align">
                                            <input
                                                className="rename-text-box"
                                                onChange={(e) => setUtils({ ...utils, componentName: { ...utils.componentName, current: e.target.value } })}
                                                value={utils.componentName?.current}
                                            />
                                            {
                                                returnIcon(
                                                    "component-name-cancel",
                                                    "Cancel update",
                                                    "CANCEL-NAME-SAVE",
                                                    "feather-x",
                                                    "",
                                                    "",
                                                    "",
                                                    "grey"
                                                )
                                            }
                                            {
                                                !_.isEmpty(utils.componentName?.current) && !_.isEqual(utils.componentName?.current, utils.componentName?.initial) &&
                                                    returnIcon(
                                                        "component-name-update",
                                                        "Update name",
                                                        "SAVE-NAME",
                                                        "feather-check",
                                                        "ml-3"
                                                    )
                                            }
                                        </div>
                                        :
                                    !_.isEmpty(utils.componentName?.current) &&
                                        <div className="vertical-center-align">
                                            <h5 className="equipment-component-header ml-3">
                                                { utils.componentName?.current }
                                            </h5>
                                            {
                                                returnIcon(
                                                    "component-name-edit",
                                                    "Edit name",
                                                    "EDIT-NAME",
                                                    "feather-edit-2",
                                                    `${isEditOngoing ? "disable-click" : "cursor-pointer"}`,
                                                    18,
                                                    18,
                                                    isEditOngoing ? "grey" : "#075978"
                                                )
                                            }
                                        </div>
                            }
                        </Card.Header>
                        <Card.Body className="p-0 h-100">
                            <PerfectScrollbar style={{ height: "580px" }}>
                                {
                                    !_.isEmpty(selectedComponent?.id) && !_.isEmpty(utils.id) && !getComponentSensorQuery.isFetching ?
                                        <>
                                            <SectionHeader
                                                title={"Component Information"}
                                                headerComponent={
                                                    <>
                                                        {
                                                            !isLockedProfile && !utils.isEditActive &&
                                                                returnIcon(
                                                                    "Component-Revert",
                                                                    "Revert",
                                                                    "REVERT",
                                                                    "feather-rotate-ccw",
                                                                    `${isEditOngoing ? "disable-click text-color-grey" : ""} mt-1 mr-3`,
                                                                    "",
                                                                    "",
                                                                    isEditOngoing ? "grey" : "#075978"
                                                                )
                                                        }
                                                        {
                                                            !isLockedProfile && !utils.isEditActive &&
                                                                returnIcon(
                                                                    "Edit-component",
                                                                    "Edit",
                                                                    "EDIT-METADATA",
                                                                    "feather-edit",
                                                                    `${isEditOngoing ? "disable-click text-color-grey" : ""} mt-1 mr-3`,
                                                                    "",
                                                                    "",
                                                                    isEditOngoing ? "grey" : "#075978"
                                                                )
                                                        }
                                                        {
                                                            !updateComponentQuery.isFetching && utils.isEditActive &&
                                                                returnIcon(
                                                                    "Component-Cancel",
                                                                    "Cancel",
                                                                    "CANCEL-METADATA-SAVE",
                                                                    "feather-x",
                                                                    `${isEditOngoing ? "text-color-grey" : "text-primary"} mt-1 mr-3`,
                                                                    "",
                                                                    "",
                                                                    "grey"
                                                                )
                                                        }
                                                        {
                                                            !updateComponentQuery.isFetching && !_.isEqual(utils.metadata, utils.metadataInitial) && utils.isEditActive &&
                                                                returnIcon(
                                                                    "Component-Save",
                                                                    "Save",
                                                                    "SAVE-METADATA",
                                                                    "feather-save",
                                                                    "mt-1 mr-3"
                                                                )
                                                        }
                                                    </>
                                                }
                                            />
                                            {
                                                !_.isEmpty(utils.metadata) || !_.isEmpty(utils.categoryMetadata) ?
                                                    <>
                                                        {
                                                            updateComponentQuery.isFetching ?
                                                                <div className="w-100 text-center my-2">
                                                                    <ClipLoader color="#075978" />
                                                                </div>
                                                                    :
                                                                <>
                                                                    {
                                                                        Object.keys({ ...utils.categoryMetadata, ...utils.metadata }).map((key, ind) =>
                                                                            <>
                                                                                <div key={ind} className="mx-2 my-1 flex-space-even">
                                                                                    <div className="d-flex w-50">
                                                                                        {
                                                                                            utils.isEditActive && !_.isEmpty(utils.metadata[key]) &&
                                                                                                <Icon
                                                                                                    className="mt-1 mr-2 component-info-remove"
                                                                                                    icon={"feather-minus-circle"}
                                                                                                    onClick={() => setUtils({ ...utils, metadata: _.omit(utils.metadata, key) })}
                                                                                                />
                                                                                        }
                                                                                        <div>
                                                                                            {key}
                                                                                        </div>
                                                                                    </div>
                                                                                    <div className="component-info-value mr-2">
                                                                                        {
                                                                                            utils.isEditActive ?
                                                                                                <>
                                                                                                    {
                                                                                                        !_.isEmpty(dropDowns[key]) ?
                                                                                                            <select
                                                                                                                onChange={e => setUtils({ ...utils, metadata: { ...utils.metadata, [`${key}`]: e.target.value } })}
                                                                                                                className="edit-dropdown text-left"
                                                                                                            >
                                                                                                                <option hidden disabled selected>Select</option>
                                                                                                                {
                                                                                                                    dropDowns[key].map(option =>
                                                                                                                        <option
                                                                                                                            selected={option === utils.metadata[key]}
                                                                                                                            value={option}
                                                                                                                        >
                                                                                                                            {option}
                                                                                                                        </option>
                                                                                                                    )
                                                                                                                }
                                                                                                            </select>
                                                                                                                :
                                                                                                            <div>
                                                                                                                <input
                                                                                                                    className="component-info-input"
                                                                                                                    value={utils?.metadata[key] || utils?.categoryMetadata[key]}
                                                                                                                    onChange={e => setUtils({ ...utils, metadata: { ...utils.metadata, [`${key}`]: e.target.value } })}
                                                                                                                />
                                                                                                            </div>
                                                                                                    }
                                                                                                    {
                                                                                                        !_.isEmpty(utils.typeValidationErrors?.[key]?.expected) &&
                                                                                                            <div className="component-info-validation">
                                                                                                                Provide a {utils.typeValidationErrors[key]?.expected} value
                                                                                                                {
                                                                                                                    !_.isEmpty(utils.typeValidationErrors[key]?.example) ?
                                                                                                                        `, Example : ${utils.typeValidationErrors[key]?.example}`
                                                                                                                            :
                                                                                                                        ""
                                                                                                                }
                                                                                                            </div>
                                                                                                    }
                                                                                                </>
                                                                                                    :
                                                                                                <div
                                                                                                    className={`
                                                                                                        ${
                                                                                                            !_.isEmpty(utils.installTimeMetadata?.[key]) &&
                                                                                                            utils.metadata?.[key] !== utils.installTimeMetadata?.[key] ?
                                                                                                                "color-red"
                                                                                                                    :
                                                                                                                "color-grey"
                                                                                                        }`
                                                                                                    }
                                                                                                >
                                                                                                    {utils?.metadata[key]?.toString() || utils?.categoryMetadata[key]?.toString()}
                                                                                                </div>
                                                                                        }
                                                                                    </div>
                                                                                </div>
                                                                                <hr className="underLine" />
                                                                            </>
                                                                        )
                                                                    }
                                                                </>
                                                        }
                                                    </>
                                                        :
                                                    <center className="p-2 font-weight-bold">
                                                        NO COMPONENT INFORMATION FOUND
                                                    </center>
                                            }
                                            {
                                                utils.isEditActive && !updateComponentQuery.isFetching &&
                                                    (
                                                        utils.showAddMoreInfo ?
                                                            <AddMoreInformationField
                                                                onCancel={() => setUtils({ ...utils, showAddMoreInfo: false })}
                                                                addInformation={(label, value) => 
                                                                    setUtils({ ...utils, showAddMoreInfo: false, metadata: { ...utils.metadata, [`${label}`]: value } })
                                                                }
                                                            />
                                                                :
                                                            <div className="text-center">
                                                                <Button
                                                                    onClick={() => setUtils({ ...utils, showAddMoreInfo: true })}
                                                                    style={{ "height": "30px" }}
                                                                    className="w-75 p-1 mt-3 mb-2 text-center"
                                                                >
                                                                    Add more information
                                                                </Button>
                                                            </div>
                                                    )
                                            }
                                            <SectionHeader
                                                title={"Bearings"}
                                                headerComponent={
                                                    <>
                                                        {
                                                            !isLockedProfile && !utils.isEditActive &&
                                                                returnIcon(
                                                                    "Add-Bearing",
                                                                    "Add Bearing",
                                                                    "ADD-BEARING",
                                                                    "feather-plus-circle",
                                                                    `${isEditOngoing && "disable-click text-color-grey"} mt-1 mr-3`,
                                                                    "",
                                                                    "",
                                                                    isEditOngoing ? "grey" : "#075978"
                                                                )
                                                        }
                                                    </>
                                                }
                                            />

                                            <BearingList bearings={bearings} componentId={selectedComponent?.id} />

                                            <div style={{ "borderBottom": "2px solid #075978" }} />
                                            <ImageCarousel
                                                loading={getComponentSensorQuery.isFetching}
                                                target={IMAGE_OPTIONS_TYPE[1]}
                                                title={"Component Images"}
                                            />

                                            <SensorAndBearingList />

                                            {
                                                utils.showRevertPopup &&
                                                    <ComponentRevertPopup
                                                        close={() => setUtils({ ...utils, showRevertPopup: false })} id={selectedComponent?.id}
                                                    />
                                            }
                                        </>
                                            :
                                        <div className="equipment-list-flex-box equipment-scroll-viewport-height px-3">
                                            <center>
                                                {
                                                    getComponentSensorQuery.isFetching ?
                                                        <ClipLoader color="#075978" />
                                                            :
                                                        <b>
                                                            PLEASE SELECT AN EQUIPMENT AND COMPONENT TO VIEW LIST OF SENSORS
                                                        </b>
                                                }
                                            </center>
                                        </div>
                                }
                            </PerfectScrollbar>
                        </Card.Body>

                    </Card>
                </Col>
                <Modal
                    backdrop="static"
                    onHide={() => setUtils({ ...utils, showBearingForm: false })}
                    show={utils.showBearingForm}
                    size="lg"
                    
                >
                    <Modal.Header closeButton>
                        <Modal.Title>Add new bearing</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <BearingForm
                            availableLocations = {utils.availableLocations}
                            setBearingForm = {utils.showBearingForm}
                        />
                    </Modal.Body>
                </Modal>
            </>
    );
}

export default ComponentInformation;